import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../components/ui/button'

const CONTEXTS = ['fasting', 'pre_meal', 'post_meal', 'before_bed', 'other']

// Kind-aware inline form for logging a vital reading. Fields depend on
// `kind`: BP shows systolic/diastolic, HR shows bpm, glucose shows a
// decimal mg/dL input. Context + notes are shared.
export function VitalsForm({ kind, onSave, onCancel, submitting }) {
  const { t } = useTranslation()
  const [systolic, setSystolic] = useState('')
  const [diastolic, setDiastolic] = useState('')
  const [bpm, setBpm] = useState('')
  const [glucose, setGlucose] = useState('')
  const [context, setContext] = useState('')
  const [notes, setNotes] = useState('')

  const canSave = (() => {
    if (kind === 'blood_pressure') return systolic.trim() !== '' && diastolic.trim() !== ''
    if (kind === 'heart_rate') return bpm.trim() !== ''
    if (kind === 'glucose') return glucose.trim() !== ''
    return false
  })()

  const handleSubmit = (e) => {
    e.preventDefault()
    const payload = {
      context: context || null,
      notes: notes.trim() || null,
    }
    if (kind === 'blood_pressure') {
      const sys = Number(systolic)
      const dia = Number(diastolic)
      if (!Number.isFinite(sys) || !Number.isFinite(dia)) return
      payload.systolic = sys
      payload.diastolic = dia
    } else if (kind === 'heart_rate') {
      const v = Number(bpm)
      if (!Number.isFinite(v)) return
      payload.bpm = v
    } else if (kind === 'glucose') {
      const v = Number(glucose)
      if (!Number.isFinite(v)) return
      payload.glucose_mg_dl = v
    }
    onSave(payload)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-3 rounded-lg border border-input bg-muted/30">
      {kind === 'blood_pressure' && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-muted-foreground mb-1" dir="auto">
              {t('healthProfile.vitals.bp.systolic')}
            </label>
            <input
              type="number"
              inputMode="numeric"
              value={systolic}
              onChange={(e) => setSystolic(e.target.value)}
              required
              dir="ltr"
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1" dir="auto">
              {t('healthProfile.vitals.bp.diastolic')}
            </label>
            <input
              type="number"
              inputMode="numeric"
              value={diastolic}
              onChange={(e) => setDiastolic(e.target.value)}
              required
              dir="ltr"
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono"
            />
          </div>
        </div>
      )}

      {kind === 'heart_rate' && (
        <div>
          <label className="block text-xs text-muted-foreground mb-1" dir="auto">
            {t('healthProfile.vitals.hr.bpm')}
          </label>
          <input
            type="number"
            inputMode="numeric"
            value={bpm}
            onChange={(e) => setBpm(e.target.value)}
            required
            dir="ltr"
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono"
          />
        </div>
      )}

      {kind === 'glucose' && (
        <div>
          <label className="block text-xs text-muted-foreground mb-1" dir="auto">
            {t('healthProfile.vitals.glucose.value')} ({t('healthProfile.vitals.glucose.unit')})
          </label>
          <input
            type="number"
            step="0.1"
            inputMode="decimal"
            value={glucose}
            onChange={(e) => setGlucose(e.target.value)}
            required
            dir="ltr"
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono"
          />
        </div>
      )}

      <div>
        <label className="block text-xs text-muted-foreground mb-1" dir="auto">
          {t('healthProfile.vitals.context.label')}
        </label>
        <select
          value={context}
          onChange={(e) => setContext(e.target.value)}
          dir="auto"
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
        >
          <option value="">—</option>
          {CONTEXTS.map((c) => (
            <option key={c} value={c}>
              {t(`healthProfile.vitals.context.${c}`)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs text-muted-foreground mb-1" dir="auto">
          {t('healthProfile.vitals.notes')}
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none"
        />
      </div>

      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={submitting || !canSave} className="flex-1">
          {t('common.save')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
